import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  CheckCircle2, 
  HeartHandshake, 
  Sparkles, 
  Clock, 
  Calendar, 
  ShieldCheck, 
  Phone, 
  Mail, 
  Brain, 
  Compass, 
  Zap, 
  Heart, 
  Check, 
  FileText, 
  Download,
  Eye,
  Info
} from 'lucide-react';
import { Link } from 'react-router-dom';
import PdfResourceCard from '../components/PdfResourceCard';
import PdfModal from '../components/PdfModal';
import { siteConfig } from '../data';

const Trivlogi11 = () => {
  const [activePdfModal, setActivePdfModal] = useState(null);

  const pdfDocs = [
    {
      id: 'kort',
      title: '1-1 Kort overblik',
      subtitle: 'Når det hele fylder for meget',
      description: 'Et kort, letlæseligt overblik over den afklarende samtale, de tre søjler og det første skridt. Velegnet at give videre til en du kender.',
      url: '/_assets/1-1-kort.pdf',
      tag: 'Kort overblik (1 side)',
      highlight: true,
      badgeText: 'Anbefalet start'
    },
    {
      id: 'fuld',
      title: '1-1 Fuld forklaring',
      subtitle: 'Forståelse før forandring',
      description: 'Komplet gennemgang af TRIVlogi® 1:1-forløbet: Hvad du kan forvente, hvem det er relevant for, og de erfaringsmæssige resultater.',
      url: '/_assets/1-1-fuld-forklaring.pdf',
      tag: 'Fuld guide (2 sider)'
    },
    {
      id: 'priser',
      title: '1-1 Priser for private',
      subtitle: 'En privat pris – fordi du selv betaler',
      description: 'Overskueligt prisark over gratis afklaring, enkeltsessioner samt 3 og 6 sessioners forløb (inkl. moms).',
      url: '/_assets/1-1priser.pdf',
      tag: 'Prisoversigt'
    }
  ];

  return (
    <div className="bg-white text-zinc-900">
      {/* Hero Section */}
      <section className="relative bg-emerald-950 text-white overflow-hidden py-20 lg:py-28">
        <div className="absolute inset-0 -z-10 opacity-20">
          <img 
            src="/materiale-260501/image00001.jpg" 
            alt="TRIVlogi 1-1 baggrund" 
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-950/90 via-emerald-950/95 to-emerald-950 -z-10" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-emerald-800/80 border border-emerald-700/60 text-emerald-200 text-xs sm:text-sm font-semibold mb-6">
              <Sparkles className="w-4 h-4 text-emerald-400" />
              TRIVlogi® · Biologien bag trivsel
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white mb-6 leading-[1.1]">
              1:1 FORLØB
            </h1>
            <p className="text-xl sm:text-2xl text-emerald-100 font-medium mb-4 leading-snug">
              Forstå hvad der sker i dig. Find en vej, der giver mening for dig.
            </p>
            <p className="text-base sm:text-lg text-emerald-200/80 mb-10 leading-relaxed max-w-2xl">
              Når vi forstår kroppens, hjernens og nervesystemets reaktioner, bliver det ofte lettere at møde os selv anderledes og finde mere brugbare handlemuligheder.
            </p>

            <div className="flex flex-wrap gap-4">
              <Link
                to="/kontakt"
                className="bg-emerald-500 hover:bg-emerald-400 text-emerald-950 px-8 py-4 font-bold text-base sm:text-lg rounded-xl transition-all shadow-lg flex items-center gap-2 group"
              >
                Book en gratis afklarende samtale
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <a
                href="#materiale-pdf"
                className="bg-emerald-900/80 hover:bg-emerald-800 text-white border border-emerald-700 px-6 py-4 font-semibold text-base rounded-xl transition-all flex items-center gap-2"
              >
                <FileText className="w-5 h-5 text-emerald-300" />
                Hent &amp; se PDF-materiale
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Når det hele fylder for meget (Fra 1-1 kort.pdf) */}
      <section className="py-20 bg-emerald-50/60 border-b border-emerald-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-3xl p-8 sm:p-12 lg:p-14 shadow-xl border border-emerald-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-72 h-72 bg-emerald-100/50 rounded-full blur-3xl pointer-events-none" />

            <div className="relative z-10">
              <span className="text-xs uppercase tracking-widest font-bold text-emerald-800 bg-emerald-100/80 px-3.5 py-1.5 rounded-full inline-block mb-4">
                Et sted at starte
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold text-emerald-950 mb-3">
                Når det hele fylder for meget
              </h2>
              <p className="text-xl font-semibold text-emerald-800 mb-6">
                Du behøver ikke have styr på det hele, før du tager det første skridt.
              </p>

              <div className="space-y-4 text-base sm:text-lg text-slate-700 leading-relaxed max-w-3xl mb-8">
                <p>
                  Måske er du presset, stresset eller bare nået dertil, hvor du kan mærke, at noget skal ændre sig.
                </p>
                <p>
                  Når man ikke har det godt, kan selv det at finde ud af, hvilken hjælp man har brug for, føles som endnu en opgave. Derfor behøver du ikke starte med at beslutte dig for et forløb.
                </p>
              </div>

              {/* Callout box */}
              <div className="p-6 sm:p-8 bg-emerald-950 text-white rounded-2xl mb-8 border border-emerald-800">
                <h3 className="text-xl sm:text-2xl font-bold mb-3 text-white">
                  Du kan starte med en gratis og uforpligtende samtale.
                </h3>
                <p className="text-emerald-100 text-base leading-relaxed mb-6">
                  Her kan du fortælle lidt om det, der fylder. Du behøver ikke have de rigtige ord eller kunne forklare det hele. Vi tager udgangspunkt i dér, hvor du er, og finder sammen ud af, om jeg og TRIVlogi® er det rigtige næste skridt for dig.
                </p>
                <div className="flex flex-wrap items-center gap-4">
                  <Link
                    to="/kontakt"
                    className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold px-6 py-3 rounded-xl transition-all text-sm sm:text-base shadow-md"
                  >
                    Book gratis samtale (20 min)
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                  <button
                    type="button"
                    onClick={() => setActivePdfModal(pdfDocs[0])}
                    className="inline-flex items-center gap-2 bg-emerald-900/90 hover:bg-emerald-800 text-white font-medium px-5 py-3 rounded-xl transition-colors text-sm border border-emerald-700"
                  >
                    <Eye className="w-4 h-4 text-emerald-300" />
                    Læs kort PDF-version
                  </button>
                </div>
              </div>

              {/* 3 Steps summary */}
              <div className="grid sm:grid-cols-3 gap-4 pt-4 border-t border-zinc-100">
                <div className="p-4 bg-zinc-50 rounded-xl border border-zinc-200/80">
                  <span className="font-bold text-xs uppercase tracking-wider text-emerald-900 block mb-1">Biologi</span>
                  <p className="text-sm text-slate-700">Forstå hvad der sker i dig.</p>
                </div>
                <div className="p-4 bg-zinc-50 rounded-xl border border-zinc-200/80">
                  <span className="font-bold text-xs uppercase tracking-wider text-emerald-900 block mb-1">Mindset</span>
                  <p className="text-sm text-slate-700">Forstå dine mønstre og reaktioner.</p>
                </div>
                <div className="p-4 bg-zinc-50 rounded-xl border border-zinc-200/80">
                  <span className="font-bold text-xs uppercase tracking-wider text-emerald-900 block mb-1">Strategi</span>
                  <p className="text-sm text-slate-700">Find konkrete skridt, der passer til dit liv.</p>
                </div>
              </div>

              <div className="mt-8 pt-6 border-t border-zinc-100 flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-xs sm:text-sm text-slate-500">
                <p>
                  11 års erfaring med mennesker og trivsel. Min erfaring er, at forståelse ofte skaber mere ro og flere handlemuligheder.
                </p>
                <p className="italic text-emerald-900 font-medium">
                  Kender du én, der kæmper lige nu? Du er meget velkommen til at give denne side eller den korte PDF videre.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Forståelse før forandring & Modellen */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <span className="text-xs uppercase tracking-widest font-bold text-emerald-800 bg-emerald-50 px-3 py-1 rounded-full inline-block mb-3">
              Kernen i forløbet
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-zinc-900 mb-6">
              Forståelse før forandring
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed">
              Et TRIVlogi® 1:1-forløb er for dig, der mærker stress, pres, mistrivsel, fastlåste mønstre eller manglende ro og retning. Mit udgangspunkt er ikke, at du skal tage dig mere sammen. Når vi forstår kroppens og hjernens reaktioner, bliver det ofte lettere at møde os selv anderledes og finde mere brugbare handlemuligheder.
            </p>
          </div>

          {/* 3 Pillar Cards */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-8 relative flex flex-col hover:border-emerald-300 transition-colors">
              <div className="w-12 h-12 rounded-xl bg-emerald-900 text-white flex items-center justify-center font-bold text-lg mb-6 shadow-md">
                1
              </div>
              <h3 className="text-2xl font-bold text-emerald-950 mb-3">BIOLOGI</h3>
              <p className="text-slate-700 leading-relaxed">
                Forstå kroppens, hjernens og nervesystemets reaktioner.
              </p>
            </div>

            <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-8 relative flex flex-col hover:border-emerald-300 transition-colors">
              <div className="w-12 h-12 rounded-xl bg-emerald-800 text-white flex items-center justify-center font-bold text-lg mb-6 shadow-md">
                2
              </div>
              <h3 className="text-2xl font-bold text-emerald-950 mb-3">MINDSET</h3>
              <p className="text-slate-700 leading-relaxed">
                Undersøg mønstre, fortolkninger og forventninger, der påvirker dig.
              </p>
            </div>

            <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-8 relative flex flex-col hover:border-emerald-300 transition-colors">
              <div className="w-12 h-12 rounded-xl bg-emerald-700 text-white flex items-center justify-center font-bold text-lg mb-6 shadow-md">
                3
              </div>
              <h3 className="text-2xl font-bold text-emerald-950 mb-3">STRATEGI</h3>
              <p className="text-slate-700 leading-relaxed">
                Omsæt forståelsen til konkrete handlinger, der kan fungere i din hverdag.
              </p>
            </div>
          </div>

          {/* Hvad kan du forvente? */}
          <div className="bg-emerald-950 text-white rounded-3xl p-8 sm:p-12 lg:p-16 mb-16 shadow-xl">
            <h3 className="text-2xl sm:text-3xl font-bold mb-10 text-center">
              Hvad kan du forvente?
            </h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="p-6 bg-emerald-900/60 rounded-2xl border border-emerald-800">
                <h4 className="font-bold text-lg text-emerald-300 mb-2">ET STED AT STARTE</h4>
                <p className="text-sm text-emerald-100/90 leading-relaxed">
                  Du behøver ikke have en færdig forklaring. Vi skaber sammen overblik over det, der fylder.
                </p>
              </div>

              <div className="p-6 bg-emerald-900/60 rounded-2xl border border-emerald-800">
                <h4 className="font-bold text-lg text-emerald-300 mb-2">FORSTÅELSE</h4>
                <p className="text-sm text-emerald-100/90 leading-relaxed">
                  Dine reaktioner sættes ind i en biologisk og menneskelig sammenhæng, så de bliver lettere at forstå.
                </p>
              </div>

              <div className="p-6 bg-emerald-900/60 rounded-2xl border border-emerald-800">
                <h4 className="font-bold text-lg text-emerald-300 mb-2">KONKRETE VÆRKTØJER</h4>
                <p className="text-sm text-emerald-100/90 leading-relaxed">
                  Du får strategier, du kan afprøve mellem samtalerne og bruge i din hverdag.
                </p>
              </div>

              <div className="p-6 bg-emerald-900/60 rounded-2xl border border-emerald-800">
                <h4 className="font-bold text-lg text-emerald-300 mb-2">DIT TEMPO</h4>
                <p className="text-sm text-emerald-100/90 leading-relaxed">
                  Vi arbejder i et tempo, hvor du kan følge med og mærke, hvad der faktisk hjælper.
                </p>
              </div>
            </div>
          </div>

          {/* Hvem forløbet er relevant for */}
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs uppercase tracking-widest font-bold text-emerald-800 bg-emerald-50 px-3 py-1 rounded-full inline-block mb-3">
                Målgruppe
              </span>
              <h3 className="text-2xl sm:text-3xl font-bold text-zinc-900 mb-6">
                Et forløb kan være relevant, hvis du...
              </h3>
              <div className="space-y-3.5">
                {[
                  'mærker stress, uro eller overbelastning',
                  'har mistet energi, retning eller overskud',
                  'sidder fast i mønstre, du gerne vil forstå',
                  'har svært ved at sætte grænser eller mærke egne behov',
                  'er på vej tilbage efter stress',
                  'vil reagere tidligere, før belastningen vokser'
                ].map((item, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 rounded-xl bg-zinc-50 border border-zinc-200/80">
                    <CheckCircle2 className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-800 font-medium text-sm sm:text-base">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-emerald-50/70 p-8 sm:p-10 rounded-3xl border border-emerald-200">
              <span className="text-xs uppercase tracking-widest font-bold text-emerald-900 block mb-2">
                Om tilgangen
              </span>
              <h4 className="text-2xl font-bold text-emerald-950 mb-4">
                11 års erfaring med mennesker og trivsel
              </h4>
              <p className="text-slate-700 leading-relaxed mb-6">
                TRIVlogi® er vokset ud af mit arbejde med mennesker gennem 11 år. Jeg kombinerer biologisk forståelse, samtale, refleksion og konkrete værktøjer. Mennesket kommer før metoden, og forløbet tilpasses det, der faktisk fylder hos dig.
              </p>
              <div className="p-5 bg-white rounded-2xl border border-emerald-200 text-sm text-emerald-900">
                <p className="font-semibold mb-1">Michael Lunde</p>
                <p className="text-slate-600">Certificeret Coach &amp; StressMentor · Kolding &amp; Online</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Erfaringer & Resultater */}
      <section className="py-20 bg-zinc-50 border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <span className="text-xs uppercase tracking-widest font-bold text-emerald-800 bg-emerald-100/70 px-3 py-1 rounded-full inline-block mb-3">
              Udbytte
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-zinc-900 mb-4">
              Hvad siger mine erfaringer om resultaterne?
            </h2>
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
              Mennesker og forløb er forskellige, så resultater kan ikke sættes på en fast formel. Men gennem mit arbejde er der nogle udviklinger, jeg ofte oplever, når biologisk forståelse kombineres med mindset og konkrete strategier.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-14">
            {[
              {
                title: 'MERE RO',
                text: 'Når reaktionerne bliver forståelige, oplever mange, at noget af kampen med dem selv falder til ro.'
              },
              {
                title: 'STØRRE OVERBLIK',
                text: 'Det bliver lettere at se, hvad der presser, hvad man kan påvirke, og hvor noget skal ændres.'
              },
              {
                title: 'BEDRE SELVFORSTÅELSE',
                text: 'Mange bliver bedre til at genkende egne signaler, behov og mønstre tidligere.'
              },
              {
                title: 'MERE HANDLEKRAFT',
                text: 'Når problemet bliver mere konkret, bliver næste skridt ofte også mere konkret.'
              },
              {
                title: 'TYDELIGERE GRÆNSER',
                text: 'Det kan blive lettere at mærke, hvornår man skal sige til, sige fra eller gøre noget anderledes.'
              },
              {
                title: 'EN BÆREDYGTIG VEJ VIDERE',
                text: 'Ved stress er min erfaring, at forståelse og konkrete strategier kan støtte vejen tilbage til mere ro, overskud og arbejdsevne.'
              }
            ].map((res, i) => (
              <div key={i} className="bg-white p-7 rounded-2xl border border-zinc-200 shadow-sm flex flex-col justify-between">
                <div>
                  <h4 className="text-lg font-bold text-emerald-950 mb-2.5">{res.title}</h4>
                  <p className="text-sm sm:text-base text-slate-700 leading-relaxed">{res.text}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Filosofi Callout */}
          <div className="max-w-4xl mx-auto p-8 sm:p-10 bg-emerald-900 text-white rounded-3xl text-center shadow-lg border border-emerald-800">
            <p className="text-xl sm:text-2xl font-bold mb-3">
              Målet er ikke, at du bliver afhængig af samtaler.
            </p>
            <p className="text-base sm:text-lg text-emerald-100 leading-relaxed">
              Målet er, at du gradvist får større forståelse af dig selv og redskaber, du kan tage med videre – så du bliver bedre til at opdage dine signaler, forstå dine reaktioner og handle tidligere, også efter forløbet er afsluttet.
            </p>
          </div>
        </div>
      </section>

      {/* Priser for Private (Fra 1-1priser.pdf) */}
      <section id="priser" className="py-24 bg-white border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <span className="text-xs uppercase tracking-widest font-bold text-emerald-800 bg-emerald-50 px-3 py-1 rounded-full inline-block mb-3">
              Gennemskuelige priser
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-zinc-900 mb-3">
              PRISER · 1:1 FOR PRIVATE
            </h2>
            <p className="text-xl font-semibold text-emerald-800 mb-4">
              Hjælp skal også være mulig, når livet allerede presser.
            </p>
            <div className="text-slate-600 text-base leading-relaxed space-y-2">
              <p className="font-semibold text-zinc-800">
                En privat pris – fordi du selv betaler
              </p>
              <p>
                Når du selv betaler for et TRIVlogi®-forløb, er prisen lavere end ved virksomhedsbetalte forløb. Jeg ønsker, at økonomien så vidt muligt ikke bliver den ekstra belastning, der gør det sværere at række ud efter hjælp. Du kan starte med én samtale eller vælge et forløb – og altid begynde med en gratis afklarende samtale.
              </p>
            </div>
          </div>

          {/* Pricing Grid */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {/* Pakke 1 */}
            <div className="bg-emerald-50/70 border-2 border-emerald-500 rounded-3xl p-7 flex flex-col justify-between shadow-lg relative">
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-emerald-700 text-white text-xs font-bold uppercase tracking-wider px-3.5 py-1 rounded-full">
                Start her
              </div>
              <div>
                <h3 className="text-xl font-bold text-emerald-950 mb-2">GRATIS AFKLARING</h3>
                <div className="mb-4">
                  <span className="text-4xl font-black text-emerald-950">0 kr.</span>
                  <span className="text-sm font-semibold text-emerald-800 ml-2">/ 20 min.</span>
                </div>
                <p className="text-sm text-slate-700 leading-relaxed mb-6">
                  Kort og uforpligtende: Vi taler om det, der fylder, og om TRIVlogi® er det rigtige næste skridt.
                </p>
              </div>
              <Link
                to="/kontakt"
                className="w-full text-center bg-emerald-700 hover:bg-emerald-800 text-white font-bold py-3 px-4 rounded-xl transition-colors text-sm shadow-sm"
              >
                Book gratis afklaring
              </Link>
            </div>

            {/* Pakke 2 */}
            <div className="bg-white border border-zinc-200 rounded-3xl p-7 flex flex-col justify-between shadow-sm hover:border-emerald-300 transition-colors">
              <div>
                <h3 className="text-xl font-bold text-zinc-900 mb-2">ENKELT SESSION</h3>
                <div className="mb-4">
                  <span className="text-4xl font-black text-zinc-900">950 kr.</span>
                  <span className="text-sm font-medium text-slate-500 ml-2">/ 60 min.</span>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-6">
                  Til dig, der ønsker én samtale eller vil tage det én session ad gangen.
                </p>
              </div>
              <Link
                to="/kontakt"
                className="w-full text-center bg-zinc-900 hover:bg-zinc-800 text-white font-bold py-3 px-4 rounded-xl transition-colors text-sm"
              >
                Vælg enkelt session
              </Link>
            </div>

            {/* Pakke 3 */}
            <div className="bg-white border border-zinc-200 rounded-3xl p-7 flex flex-col justify-between shadow-sm hover:border-emerald-300 transition-colors">
              <div>
                <h3 className="text-xl font-bold text-zinc-900 mb-2">3 SESSIONER</h3>
                <div className="mb-1">
                  <span className="text-4xl font-black text-zinc-900">2.700 kr.</span>
                </div>
                <p className="text-xs font-semibold text-emerald-700 mb-4">
                  3 x 60 min. · 900 kr. pr. session
                </p>
                <p className="text-sm text-slate-600 leading-relaxed mb-6">
                  Et kort forløb med plads til forståelse, afprøvning og opfølgning.
                </p>
              </div>
              <Link
                to="/kontakt"
                className="w-full text-center bg-zinc-900 hover:bg-zinc-800 text-white font-bold py-3 px-4 rounded-xl transition-colors text-sm"
              >
                Vælg 3 sessioner
              </Link>
            </div>

            {/* Pakke 4 */}
            <div className="bg-emerald-950 text-white border border-emerald-800 rounded-3xl p-7 flex flex-col justify-between shadow-xl relative">
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2 bg-emerald-500 text-emerald-950 text-xs font-bold uppercase tracking-wider px-3.5 py-1 rounded-full">
                Mest valgte forløb
              </div>
              <div>
                <h3 className="text-xl font-bold text-white mb-2">6 SESSIONER</h3>
                <div className="mb-1">
                  <span className="text-4xl font-black text-emerald-400">5.100 kr.</span>
                </div>
                <p className="text-xs font-semibold text-emerald-300 mb-4">
                  6 x 60 min. · 850 kr. pr. session
                </p>
                <p className="text-sm text-emerald-100/90 leading-relaxed mb-6">
                  Et sammenhængende forløb med tid til Biologi → Mindset → Strategi.
                </p>
              </div>
              <Link
                to="/kontakt"
                className="w-full text-center bg-emerald-500 hover:bg-emerald-400 text-emerald-950 font-bold py-3 px-4 rounded-xl transition-colors text-sm shadow-md"
              >
                Vælg 6 sessioner
              </Link>
            </div>
          </div>

          {/* Pricing notes */}
          <div className="max-w-4xl mx-auto space-y-4 text-sm text-slate-600">
            <div className="p-4 bg-zinc-50 rounded-2xl border border-zinc-200/80 flex items-start gap-3">
              <Info className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-bold text-zinc-900 block mb-0.5">Privatpris og virksomhedsbetalt pris</span>
                <p>
                  Priserne her gælder, når du som privatperson selv betaler. Hvis en arbejdsplads, organisation eller virksomhed betaler, gælder erhvervspriserne under <Link to="/trivsel-i-faellesskabet#priser" className="text-emerald-800 font-semibold underline">Trivsel i fællesskabet</Link>.
                </p>
              </div>
            </div>

            <div className="p-4 bg-zinc-50 rounded-2xl border border-zinc-200/80 flex items-start gap-3">
              <Check className="w-5 h-5 text-emerald-700 flex-shrink-0 mt-0.5" />
              <div>
                <span className="font-bold text-zinc-900 block mb-0.5">Du behøver ikke beslutte dig for et forløb nu</span>
                <p>
                  Er du i tvivl om, hvad du har brug for, så start med den gratis afklarende samtale. Du kan fortælle lidt om din situation, stille spørgsmål og mærke efter, om min tilgang føles rigtig for dig.
                </p>
              </div>
            </div>

            <p className="text-center text-xs text-slate-500 pt-2">
              Alle privatpriser er angivet inkl. moms. Lokation: Kolding, Online eller efter aftale.
            </p>
          </div>
        </div>
      </section>

      {/* Første skridt er ikke et helt forløb */}
      <section className="py-20 bg-emerald-950 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="text-xs uppercase tracking-widest font-bold text-emerald-400 bg-emerald-900 px-3 py-1 rounded-full inline-block mb-3">
              Kom godt i gang
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Første skridt er ikke et helt forløb
            </h2>
            <p className="text-emerald-100 text-base sm:text-lg leading-relaxed">
              Det kan være svært at vide, om et 1:1-forløb er det rigtige, før man har talt sammen. Derfor kan du begynde med en gratis og uforpligtende afklarende samtale. Du kan fortælle lidt om det, der fylder, stille spørgsmål og få en fornemmelse af min tilgang. Sammen afklarer vi, om TRIVlogi® giver mening for dig.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <div className="bg-emerald-900/60 border border-emerald-800 p-6 rounded-2xl flex items-start gap-4">
              <div className="w-9 h-9 rounded-full bg-emerald-500 text-emerald-950 font-bold flex items-center justify-center flex-shrink-0">
                1
              </div>
              <div>
                <h4 className="font-bold text-white mb-1">Book en gratis samtale</h4>
                <p className="text-sm text-emerald-200/90 leading-relaxed">Ingen forpligtelse. Bare en første afklaring.</p>
              </div>
            </div>

            <div className="bg-emerald-900/60 border border-emerald-800 p-6 rounded-2xl flex items-start gap-4">
              <div className="w-9 h-9 rounded-full bg-emerald-500 text-emerald-950 font-bold flex items-center justify-center flex-shrink-0">
                2
              </div>
              <div>
                <h4 className="font-bold text-white mb-1">Fortæl, hvad der fylder</h4>
                <p className="text-sm text-emerald-200/90 leading-relaxed">Du behøver ikke have det hele formuleret på forhånd.</p>
              </div>
            </div>

            <div className="bg-emerald-900/60 border border-emerald-800 p-6 rounded-2xl flex items-start gap-4">
              <div className="w-9 h-9 rounded-full bg-emerald-500 text-emerald-950 font-bold flex items-center justify-center flex-shrink-0">
                3
              </div>
              <div>
                <h4 className="font-bold text-white mb-1">Mærk efter, om det føles rigtigt</h4>
                <p className="text-sm text-emerald-200/90 leading-relaxed">Tillid og kemi er en vigtig del af et personligt forløb.</p>
              </div>
            </div>
          </div>

          <div className="p-8 sm:p-10 bg-gradient-to-r from-emerald-900 via-emerald-800 to-emerald-900 rounded-3xl text-center border border-emerald-700 shadow-2xl">
            <h3 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white mb-3">
              MÅSKE ER NÆSTE SKRIDT BARE EN SAMTALE.
            </h3>
            <p className="text-emerald-100 text-base sm:text-lg mb-6 max-w-xl mx-auto">
              Tag fat i mig i dag, så finder vi et tidspunkt, der passer dig.
            </p>
            <div className="flex flex-wrap justify-center items-center gap-4">
              <Link
                to="/kontakt"
                className="bg-white hover:bg-emerald-50 text-emerald-950 font-bold px-8 py-4 rounded-xl transition-all shadow-md text-base"
              >
                Book en gratis afklarende samtale
              </Link>
              <a
                href={`tel:${siteConfig.phone}`}
                className="inline-flex items-center gap-2 bg-emerald-950/70 hover:bg-emerald-950 text-white font-semibold px-6 py-4 rounded-xl border border-emerald-600 transition-colors text-base"
              >
                <Phone className="w-4 h-4 text-emerald-400" />
                Ring på 26 36 83 06
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* PDF sektion (Vises og downloades) */}
      <section id="materiale-pdf" className="py-24 bg-zinc-50 border-t border-zinc-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <span className="text-xs uppercase tracking-widest font-bold text-emerald-800 bg-emerald-100/70 px-3 py-1 rounded-full inline-block mb-3">
              Dokumenter &amp; Ressourcer
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-zinc-900 mb-4">
              Hent &amp; Læs Materiale om 1-1 Forløb
            </h2>
            <p className="text-slate-600 text-base sm:text-lg leading-relaxed">
              Her kan du fordybe dig i materialet. Du kan både åbne og læse PDF'erne direkte i browseren eller downloade dem til din enhed.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {pdfDocs.map((doc) => (
              <PdfResourceCard
                key={doc.id}
                title={doc.title}
                description={doc.description}
                pdfUrl={doc.url}
                tag={doc.tag}
                highlight={doc.highlight}
                badgeText={doc.badgeText}
              />
            ))}
          </div>

          <div className="p-6 bg-white rounded-2xl border border-zinc-200 text-center max-w-2xl mx-auto shadow-sm">
            <p className="text-sm text-slate-600">
              Har du spørgsmål til noget af materialet? Du er altid velkommen til at kontakte mig på{' '}
              <a href="mailto:kontakt@ditlivditansvar.dk" className="text-emerald-800 font-bold underline">
                kontakt@ditlivditansvar.dk
              </a>{' '}
              eller ringe på{' '}
              <a href="tel:26368306" className="text-emerald-800 font-bold underline">
                26 36 83 06
              </a>.
            </p>
          </div>
        </div>
      </section>

      {/* Dynamic PDF Modal if active */}
      {activePdfModal && (
        <PdfModal
          isOpen={true}
          onClose={() => setActivePdfModal(null)}
          pdfUrl={activePdfModal.url}
          title={activePdfModal.title}
          description={activePdfModal.description}
        />
      )}
    </div>
  );
};

export default Trivlogi11;
